import React from 'react';
import { Link } from 'react-router-dom';
import { Rocket, Mail, Phone, MapPin, Instagram, Youtube } from 'lucide-react';
import { FaTiktok, FaSnapchat } from 'react-icons/fa';  // Added imports for TikTok and Snapchat from react-icons/fa

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-background border-t border-primary/10 ml-0 lg:ml-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Brand */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <div className="bg-primary p-1.5 -skew-x-12">
                <Rocket className="h-5 w-5 text-background" />
              </div>
              <span className="text-base font-heading font-bold text-foreground">
                DRONE CARNIVAL
              </span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              A three-day celebration of drones, robotics, and high-energy competition.
            </p>
            <div className="flex space-x-2">
              <a
                href="https://instagram.com/omandronecarnival/"  // Updated to match SocialMediaSidebar.js
                data-testid="social-instagram"
                className="p-2 border border-primary/30 hover:border-primary hover:bg-primary/10 transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4 text-primary" />
              </a>
              <a
                href="https://youtube.com/@omandronecarnival"  // Updated to match SocialMediaSidebar.js
                data-testid="social-youtube"
                className="p-2 border border-primary/30 hover:border-primary hover:bg-primary/10 transition-all duration-300"
                aria-label="YouTube"
              >
                <Youtube className="h-4 w-4 text-primary" />
              </a>
              <a
                href="https://tiktok.com/@omandronecarnival"  // Updated to match SocialMediaSidebar.js
                data-testid="social-tiktok"
                className="p-2 border border-primary/30 hover:border-primary hover:bg-primary/10 transition-all duration-300"
                aria-label="TikTok"
              >
                <FaTiktok className="h-4 w-4 text-primary" />
              </a>
              <a
                href="https://snapchat.com/add/droneoman_odc"  // Updated to match SocialMediaSidebar.js
                data-testid="social-snapchat"
                className="p-2 border border-primary/30 hover:border-primary hover:bg-primary/10 transition-all duration-300"
                aria-label="Snapchat"
              >
                <FaSnapchat className="h-4 w-4 text-primary" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xs font-heading font-bold uppercase tracking-widest text-foreground mb-4">
              Quick Links
            </h3>
            <ul className="space-y-2">
              {['About', 'Events', 'Schedule', 'Contact'].map((item) => (
                <li key={item}>
                  <a
                    href={`#${item.toLowerCase()}`}
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById(item.toLowerCase());
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    data-testid={`footer-link-${item.toLowerCase()}`}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
                  >
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-xs font-heading font-bold uppercase tracking-widest text-foreground mb-4">
              Support
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/register"
                  data-testid="footer-link-register-page"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
                >
                  Register
                </Link>
              </li>
              <li>
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.getElementById('contact');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  data-testid="footer-link-contact"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
                >
                  Contact Us
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-xs font-heading font-bold uppercase tracking-widest text-foreground mb-4">
              Contact
            </h3>
            <ul className="space-y-2">
              <li className="flex items-start space-x-3">
                <Mail className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                <a
                  href="mailto:info@skiesseven.com"
                  data-testid="contact-email"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
                >
                  info@skiesseven.com
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Phone className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">+968 9693 9694 </span>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">Muscat, Sultanate of Oman</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-6 border-t border-primary/10">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
            <p className="text-xs text-muted-foreground">
              © {currentYear} Oman Drone Carnival. All rights reserved.
            </p>
            <div className="flex space-x-4">
              <Link
                to="/legal/privacy"
                data-testid="footer-link-privacy"
                className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
              >
                Privacy
              </Link>
              <Link
                to="/legal/refund"
                data-testid="footer-link-refund"
                className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
              >
                Refund Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
